/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { dataProviderRegistry } from './form/services/DataProviderRegistry';
import { Dialog, Theme, useMediaQuery } from "@material-ui/core";
import { createStyles, makeStyles, useTheme } from "@material-ui/styles";
import React, { useEffect, useState } from "react";
import { loadUsers } from "../../db/stores/users";
import { UserDescription, UserListDescription } from "../../redux/users";
import { CURRENT_USER } from "../../utils/types";
import FacilitySelect, { FacilityQueryParams } from "./FacilitySelect";
import UserSelect from "./UserSelect";
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { AppState } from '../../redux';
import { Coop } from '../../db/stores/coops';

interface UserQueryParams {
  load: "current_user" | "list"
  certifications: string[],
  onlySelf: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    editorOverlayPaper: {
      height: "100%"
    },
    editorOverlay: {
      height: "calc(100%)"
    }
  })
);

const selectCurrentFacility = createSelector(
  (state: AppState) => state.facilities.currentFacility,
  facility => facility ?? null
);

const AppOverlay = () => {
  const [DialogContent, setDialogContent] = useState<React.ReactNode>(null);
  const [cancelHandler, setCancelHandler] = useState<() => void>();

  const handleSelectClose = () => {
    setDialogContent(null);
  };

  const coops = useSelector<AppState, Coop[]>((state: AppState) => {
    return Object.values(state.coops.items);
  });
  
  const theme: Theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const facility = useSelector(selectCurrentFacility);
  const classes = useStyles({});

  useEffect(function setupDataProviders() {
    dataProviderRegistry.register("facility", {
      getData(params: FacilityQueryParams) {
        if (params?.load === "current_facility") {
          const farmCoops: Coop[] = coops.filter((c: Coop) => {
            if (facility?.farm) {
              return c.farms!.includes(facility.farm.id) && !c.is_third_party_coop;
            }
            return false;
          });

          return Promise.resolve({
            ...facility,
            coop: farmCoops[0]
          });
        }

        const p = new Promise<object | null>(function (resolve, reject) {
          setDialogContent(
            <FacilitySelect
              params={params}
              onSelect={selection => {
                resolve(selection);
                handleSelectClose();
              }}
            />
          );

          setCancelHandler(() => () => resolve(null));
        });
        return p;
      }
      // Set the active facility in the appState
      // Read that when we call 
    });

    // TODO this should just register the getter, not the dialog. the dialog should be handled in the select components
    dataProviderRegistry.register("user", {
      getData(params: UserQueryParams) {
        if (params.load === "current_user") {
          const currUser: UserDescription = JSON.parse(
            localStorage.getItem(CURRENT_USER)!
          );
          if (params.certifications) {
            if (currUser.trainingRecords && currUser.trainingRecords.findIndex(cert =>
              params.certifications.includes(cert.type)) >= 0) {
              return Promise.resolve(currUser);
            }
            return Promise.resolve(null);
          }
          return Promise.resolve(currUser);
        }
        // check if the user is only allowed to select their self
        if (params.onlySelf) {
          const currUser: UserDescription = JSON.parse(
            localStorage.getItem(CURRENT_USER)!
          );
          if (!currUser.roles?.includes('ROLE_ADMIN') && !currUser.roles?.includes('ROLE_EVALUATOR_LIAISON')) {
            return new Promise<object | null>(function (resolve, reject) {
              setDialogContent(
                <UserSelect
                  users={[currUser]}
                  onSelect={selection => {
                    resolve(selection);
                    handleSelectClose();
                  }}
                />
              );
              setCancelHandler(() => () => resolve(null));
            });
          }
        }

        // List selection
        return (loadUsers() as Promise<UserDescription[]>)
          .then(all => {
            return all
              .filter(a => {
                return params.certifications
                  .some(c => a.certifications.hasOwnProperty(c) && (a.certifications[c].expires === null || new Date(a.certifications[c].expires) > new Date()))
              })
              .sort((a, b) => a.name.localeCompare(b.name)) as UserDescription[];
          })
          .then(function showOptions(remaining) {
            const p = new Promise<object | null>(function (resolve, reject) {
              setDialogContent(
                <UserSelect
                  users={remaining}
                  onSelect={selection => {
                    resolve(selection);
                    handleSelectClose();
                  }}
                />
              );
              setCancelHandler(() => () => resolve(null));
            });
            return p;
          })
      }
    });
  }, [facility]);

  return (
    <Dialog
      id="eval-editor-overlay"
      open={DialogContent !== null}
      maxWidth="sm"
      fullWidth
      onClose={reason => {
        if (cancelHandler) cancelHandler();
        handleSelectClose();
      }}
      fullScreen={fullScreen}
      className={classes.editorOverlay}
      classes={{
        paper: classes.editorOverlayPaper
      }}
    >
      {DialogContent || <div />}
    </Dialog>
  );
};

export default AppOverlay;
